import * as React from "react";
import { erkannterTextService, type ErkannterText } from "../../../services/erkannterTextService";
import { PdfIcons } from "./pdfIcons";
import { aktiveAuswahl, aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

/**
 * T-216: Seitenfeld „Text kopieren“ im PDF-Editor.
 *
 * Ein Scan ist nur ein Bild der Seite — markieren lässt sich darauf nichts.
 * Den Text hat KMVS für die Volltextsuche aber meist schon erkannt (Tika, bei
 * Scans per Texterkennung). Dieses Feld zeigt ihn als markierbaren Text mit
 * „Alles kopieren“; fehlt er oder ist er veraltet, stößt „Jetzt erkennen“ die
 * Erkennung an (läuft im Hintergrund, das Feld fragt nach).
 *
 * Ohne Fluent (Scope `.pdfx`), damit die Editor-Tests nichts nachmocken müssen.
 * Der Text bleibt reiner Text (React-Kinder, kein innerHTML).
 */

/** Wie oft und wie lange nachgefragt wird, solange die Erkennung läuft. */
export const NACHFRAGE_MS = 3000;
export const NACHFRAGE_MAX = 60; // 60 × 3 s = 3 Minuten

export const ErkannterTextFeld: React.FC<{ docId: number; onSchliessen: () => void }> = ({ docId, onSchliessen }) => {
    const [stand, setStand] = React.useState<ErkannterText | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [kopiert, setKopiert] = React.useState<"ja" | "manuell" | null>(null);
    const [zuLange, setZuLange] = React.useState(false);
    const [neuLaden, setNeuLaden] = React.useState(0);
    const textRef = React.useRef<HTMLDivElement | null>(null);

    // Laden — und solange die Erkennung läuft, alle 3 s nachfragen.
    React.useEffect(() => {
        const ab = new AbortController();
        let timer: ReturnType<typeof setTimeout> | undefined;
        let versuche = 0;
        const holen = async () => {
            try {
                const r = await erkannterTextService.laden(docId, ab.signal);
                if (ab.signal.aborted) return;
                setStand(r);
                setFehler(null);
                if (r.zustand === "laeuft") {
                    versuche += 1;
                    if (versuche >= NACHFRAGE_MAX) {
                        setZuLange(true);
                        return;
                    }
                    timer = setTimeout(() => void holen(), NACHFRAGE_MS);
                }
            } catch (e) {
                if (ab.signal.aborted) return;
                setFehler((e as Error).message);
            }
        };
        setZuLange(false);
        void holen();
        return () => {
            ab.abort();
            if (timer) clearTimeout(timer);
        };
    }, [docId, neuLaden]);

    const erkennen = React.useCallback(async () => {
        setFehler(null);
        setKopiert(null);
        try {
            const r = await erkannterTextService.erkennen(docId);
            setStand(r);
            // Nachfragen übernimmt der Lade-Effekt.
            setNeuLaden((n) => n + 1);
        } catch (e) {
            setFehler((e as Error).message);
        }
    }, [docId]);

    const allesMarkieren = React.useCallback(() => {
        const el = textRef.current;
        const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
        if (!el || !sel) return;
        const range = aktivesDokument().createRange();
        range.selectNodeContents(el);
        sel.removeAllRanges();
        sel.addRange(range);
    }, []);

    const kopieren = React.useCallback(async () => {
        const text = stand?.text ?? "";
        if (!text) return;
        try {
            if (!navigator.clipboard?.writeText) throw new Error("keine Zwischenablage");
            await navigator.clipboard.writeText(text);
            setKopiert("ja");
            window.setTimeout(() => setKopiert((k) => (k === "ja" ? null : k)), 2500);
        } catch {
            // Der Browser lässt uns nicht in die Zwischenablage — dann wenigstens
            // alles markieren, damit Strg+C reicht.
            allesMarkieren();
            setKopiert("manuell");
        }
    }, [stand, allesMarkieren]);

    const z = stand?.zustand;
    const erkennenKnopf = (beschriftung: string) => (
        <button type="button" className="pdfx-tf-btn" onClick={() => void erkennen()}>
            {beschriftung}
        </button>
    );

    let inhalt: React.ReactNode;
    if (fehler) {
        inhalt = (
            <div className="pdfx-tf-meldung" role="alert">
                <p>{fehler}</p>
                <button type="button" className="pdfx-tf-btn" onClick={() => setNeuLaden((n) => n + 1)}>
                    Erneut versuchen
                </button>
            </div>
        );
    } else if (!stand) {
        inhalt = (
            <div className="pdfx-tf-meldung" role="status">
                Text wird geladen…
            </div>
        );
    } else if (z === "laeuft") {
        inhalt = (
            <div className="pdfx-tf-meldung" role="status">
                {zuLange ? (
                    <>
                        <p>Die Erkennung dauert länger als erwartet. Bitte schließen Sie das Feld und öffnen Sie es später noch einmal.</p>
                        <button type="button" className="pdfx-tf-btn" onClick={() => setNeuLaden((n) => n + 1)}>
                            Nachsehen
                        </button>
                    </>
                ) : (
                    <p>Text wird erkannt… Bei mehrseitigen Scans kann das eine Minute dauern.</p>
                )}
            </div>
        );
    } else if (z === "offen") {
        inhalt = (
            <div className="pdfx-tf-meldung">
                <p>Für dieses Dokument wurde noch kein Text erkannt.</p>
                {erkennenKnopf("Jetzt erkennen")}
            </div>
        );
    } else if (z === "fehler") {
        inhalt = (
            <div className="pdfx-tf-meldung">
                <p>Die Texterkennung hat bei diesem Dokument nicht geklappt.</p>
                {erkennenKnopf("Noch einmal versuchen")}
            </div>
        );
    } else if (z === "leer") {
        inhalt = (
            <div className="pdfx-tf-meldung">
                <p>Auf diesem Dokument wurde kein Text gefunden.</p>
                {erkennenKnopf("Noch einmal mit Texterkennung versuchen")}
            </div>
        );
    } else if (z === "nichtUnterstuetzt") {
        inhalt = <div className="pdfx-tf-meldung">Für diese Dateiart gibt es keine Texterkennung.</div>;
    } else if (z === "zuGross") {
        inhalt = <div className="pdfx-tf-meldung">Die Datei ist zu groß für die Texterkennung.</div>;
    } else {
        inhalt = (
            <>
                {stand.aktuell === false && (
                    <div className="pdfx-tf-hinweis warn">
                        <span>Der erkannte Text gehört zu einer älteren Fassung dieses Dokuments.</span>
                        {erkennenKnopf("Jetzt erkennen")}
                    </div>
                )}
                {stand.methode === "ocr" && (
                    <div className="pdfx-tf-hinweis">Automatisch erkannt — bitte vor der Übernahme prüfen.</div>
                )}
                <div className="pdfx-tf-aktion">
                    <button type="button" className="pdfx-tf-btn primary" onClick={() => void kopieren()}>
                        {PdfIcons.kopieren}
                        Alles kopieren
                    </button>
                    {kopiert === "ja" && (
                        <span className="pdfx-tf-ok" role="status">
                            Kopiert
                        </span>
                    )}
                    {kopiert === "manuell" && (
                        <span className="pdfx-tf-ok" role="status">
                            Text ist markiert — mit Strg+C kopieren
                        </span>
                    )}
                </div>
                <div ref={textRef} className="pdfx-tf-text" tabIndex={0} aria-label="Erkannter Text">
                    {stand.text}
                </div>
            </>
        );
    }

    return (
        <aside className="pdfx-textfeld" aria-label="Erkannter Text">
            <div className="pdfx-tf-kopf">
                <span className="pdfx-tf-titel">Erkannter Text</span>
                <button type="button" className="pdfx-tf-zu" onClick={onSchliessen} aria-label="Schließen" title="Schließen">
                    {PdfIcons.schliessen}
                </button>
            </div>
            <div className="pdfx-tf-rumpf">{inhalt}</div>
        </aside>
    );
};
